import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import StatTileWidget from "./widgets/StatTileWidget";
import ChartWidget from "./widgets/ChartWidget";
import TableEmbedWidget from "./widgets/TableEmbedWidget";
import TextWidget from "./widgets/TextWidget";
import FormWidget from "./widgets/FormWidget";
import ButtonLinkWidget from "./widgets/ButtonLinkWidget";
import CustomScriptWidget from "./widgets/CustomScriptWidget";
import ImageWidget from "./widgets/ImageWidget";
import HeroWidget from "./widgets/HeroWidget";
import FeatureGridWidget from "./widgets/FeatureGridWidget";

const WIDTH_CLASSES = {
  full: "w-full",
  two_thirds: "w-full md:w-[calc(66.667%-0.333rem)]",
  half: "w-full md:w-[calc(50%-0.5rem)]",
  third: "w-full md:w-[calc(33.333%-0.667rem)]",
  quarter: "w-full md:w-[calc(25%-0.75rem)]",
};

// Shared by both PageView.js (embedded, inside the admin Dashboard shell)
// and PortalView.js (standalone, own branded layout) -- the widget
// rendering itself doesn't care which chrome it's inside, only `basePath`
// changes (so button_link/form on_success navigation lands on
// /dashboard/pages/<slug> vs /portal/<slug> correctly).
const PageWidgetsRenderer = ({ widgets, slug, basePath, apiBaseUrl, token }) => {
  const navigate = useNavigate();
  const resolveSlugPath = (targetSlug) => `${basePath}/${targetSlug}`;

  const tabs = useMemo(() => {
    const names = [];
    (widgets || []).forEach((w) => {
      if (w.tab_name && !names.includes(w.tab_name)) names.push(w.tab_name);
    });
    return names;
  }, [widgets]);

  const [activeTab, setActiveTab] = useState(tabs[0] || null);

  const renderWidget = (widget) => {
    switch (widget.widget_type) {
      case "stat_tile":
        return <StatTileWidget widget={widget} />;
      case "chart":
        return <ChartWidget widget={widget} />;
      case "table_embed":
        return <TableEmbedWidget widget={widget} />;
      case "text":
        return <TextWidget widget={widget} />;
      case "form":
        return (
          <FormWidget
            widget={widget}
            slug={slug}
            apiBaseUrl={apiBaseUrl}
            token={token}
            onSuccessNavigate={(targetSlug) => navigate(resolveSlugPath(targetSlug))}
          />
        );
      case "button_link":
        return <ButtonLinkWidget widget={widget} resolveSlugPath={resolveSlugPath} />;
      case "custom_script":
        return <CustomScriptWidget widget={widget} apiBaseUrl={apiBaseUrl} token={token} navigate={navigate} />;
      case "image":
        return <ImageWidget widget={widget} />;
      case "hero":
        return <HeroWidget widget={widget} resolveSlugPath={resolveSlugPath} />;
      case "feature_grid":
        return <FeatureGridWidget widget={widget} />;
      default:
        return null;
    }
  };

  const widgetsToShow = tabs.length
    ? (widgets || []).filter((w) => !w.tab_name || w.tab_name === activeTab)
    : widgets || [];

  return (
    <div>
      {tabs.length > 0 && (
        <div className="flex gap-2 mb-4 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeTab === tab
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-4">
        {widgetsToShow.map((widget) => (
          <div key={widget.id} className={WIDTH_CLASSES[widget.sip_width] || WIDTH_CLASSES.full}>
            {renderWidget(widget)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PageWidgetsRenderer;
